/**
 * WorkstationPulsePage — /admin/workstation-pulse
 *
 * Founder cockpit for the VOS Studio Agent (Tauri tray app). Shows:
 *
 *  • Live grid of every registered workstation (online/offline, current
 *    state, what app the artist has in foreground, which .blend they're
 *    on, last-seen timestamp).
 *  • Real-time DLP event feed (USB plugged, screenshot taken, entertainment
 *    site blocked, etc.) — listens to the new "dlp_event" websocket type.
 *  • Auto-refreshes the workstation grid every 15 s.
 *
 * Access: master_admin / production_manager / hr_manager / coo.
 *
 * Phase 1 of the agent rollout (Feb 16, 2026 founder decisions
 * 1b + 2b + 3c + 5d). Phase 2 (KPI heat-map) lives on /admin/kpi-cockpit.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import useRealtime from '@/hooks/useRealtime';
import {
  Monitor, Cpu, Activity, Clock, Wifi, WifiOff, AlertTriangle,
  Usb, Camera, Printer, Cloud, Shield, Eye, RefreshCw, Filter,
} from 'lucide-react';

const DLP_ICONS = {
  usb_plug:            Usb,
  usb_copy:            Usb,
  screenshot:          Camera,
  print:               Printer,
  screen_share:        Monitor,
  cloud_sync_detected: Cloud,
  vpn_detected:        Shield,
  warp_disabled:       Shield,
  agent_uninstall_attempt: AlertTriangle,
};
const SEVERITY_TONES = {
  info:  'text-zinc-400 bg-zinc-800/60 border-zinc-700',
  warn:  'text-amber-300 bg-amber-500/15 border-amber-500/40',
  block: 'text-rose-300 bg-rose-500/15 border-rose-500/40',
};

// ─── tiny helpers ────────────────────────────────────────────────────
function relativeTime(iso) {
  if (!iso) return 'never';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function StateDot({ state, stale }) {
  if (stale || state === 'offline') return <span className="inline-block w-2 h-2 rounded-full bg-zinc-600" title="offline" />;
  if (state === 'idle')              return <span className="inline-block w-2 h-2 rounded-full bg-amber-500" title="idle" />;
  if (state === 'paused')            return <span className="inline-block w-2 h-2 rounded-full bg-sky-500" title="paused by user" />;
  return <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" title="active" />;
}

// ─── Workstation card ───────────────────────────────────────────────
function WorkstationCard({ ws }) {
  const isOffline = ws.stale || !ws.online || ws.current_state === 'offline';
  return (
    <div
      data-testid={`ws-card-${ws.id}`}
      className={`p-4 rounded-xl border ${isOffline ? 'bg-zinc-950/60 border-zinc-800' : 'bg-zinc-900 border-zinc-800 hover:border-zinc-700'} transition-colors`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Monitor className={`w-4 h-4 ${isOffline ? 'text-zinc-600' : 'text-emerald-400'} shrink-0`} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-zinc-200 truncate">{ws.hostname}</p>
            <p className="text-[11px] text-zinc-500 truncate">{ws.user_email}</p>
          </div>
        </div>
        {isOffline
          ? <WifiOff className="w-4 h-4 text-zinc-600" />
          : <Wifi    className="w-4 h-4 text-emerald-400" />}
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center gap-2 text-[11px]">
          <StateDot state={ws.current_state} stale={ws.stale} />
          <span className="text-zinc-400 capitalize">{ws.stale ? 'stale' : (ws.current_state || 'unknown')}</span>
          <span className="text-zinc-600">·</span>
          <span className="text-zinc-500" title={ws.last_seen}>{relativeTime(ws.last_seen)}</span>
        </div>

        {ws.current_app && (
          <div className="flex items-center gap-2 text-[11px] text-zinc-400">
            <Activity className="w-3 h-3 text-amber-400" />
            <span className="font-mono truncate">{ws.current_app}</span>
          </div>
        )}

        {ws.blender_running && ws.blender_file && (
          <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1 truncate" title={ws.blender_file}>
            🎬 {ws.blender_file.split(/[/\\]/).pop()}
          </div>
        )}

        <div className="flex items-center justify-between text-[10px] text-zinc-500 pt-1 border-t border-zinc-800 mt-2">
          <span>{ws.os} · v{ws.agent_version || '?'}</span>
          {(ws.cpu_pct || ws.gpu_pct) ? (
            <span className="font-mono">CPU {Math.round(ws.cpu_pct || 0)}% · GPU {Math.round(ws.gpu_pct || 0)}%</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ─── DLP event row ──────────────────────────────────────────────────
function DLPRow({ evt }) {
  const Icon = DLP_ICONS[evt.kind] || Shield;
  const tone = SEVERITY_TONES[evt.severity] || SEVERITY_TONES.info;
  return (
    <div data-testid={`dlp-row-${evt.id || evt.ts}`}
         className={`flex items-start gap-3 px-3 py-2 rounded-md border ${tone}`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          <span className="font-semibold capitalize">{evt.kind.replace(/_/g, ' ')}</span>
          <span className="text-zinc-500">·</span>
          <span className="text-zinc-300">{evt.user_email || 'unknown'}</span>
          <span className="text-zinc-500">·</span>
          <span className="text-zinc-500 font-mono">{evt.hostname}</span>
          <span className="text-zinc-600 ml-auto">{relativeTime(evt.ts)}</span>
        </div>
        {evt.detail && <p className="text-[11px] text-zinc-400 mt-0.5 truncate">{evt.detail}</p>}
        {evt.path   && <p className="text-[10px] text-zinc-500 mt-0.5 font-mono truncate">{evt.path}</p>}
      </div>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────
export default function WorkstationPulsePage() {
  const { api } = useAuth();
  const [workstations, setWorkstations] = useState([]);
  const [dlpEvents, setDLPEvents]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [filter, setFilter]             = useState('all');     // all | online | idle | offline
  const [lastRefresh, setLastRefresh]   = useState(null);

  const loadWorkstations = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/workstations', {
        params: { include_offline: true },
      });
      setWorkstations(data.workstations || []);
      setLastRefresh(new Date());
    } catch (e) {
      // 403 = wrong role; everything else is transient.
      if (e?.response?.status === 403) setWorkstations([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadDLPFeed = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/dlp-feed', { params: { limit: 50 } });
      setDLPEvents(data.events || []);
    } catch { /* ignore */ }
  }, [api]);

  // Initial load + 15s poll for the grid (heartbeats land every 60s but
  // we want the founder to feel the dashboard is live).
  useEffect(() => {
    loadWorkstations();
    loadDLPFeed();
    const t = setInterval(() => { loadWorkstations(); }, 15000);
    return () => clearInterval(t);
  }, [loadWorkstations, loadDLPFeed]);

  // Realtime: DLP events arrive via WebSocket (from agent_api.py's
  // realtime_push). Prepend so the newest sits on top.
  useRealtime(useCallback((event) => {
    if (event?.type !== 'dlp_event') return;
    const e = event.event || {};
    e.id = event.id;
    setDLPEvents((prev) => [e, ...prev].slice(0, 200));
  }, []));

  const filtered = useMemo(() => {
    if (filter === 'all') return workstations;
    if (filter === 'online')  return workstations.filter(w => !w.stale && w.online && w.current_state !== 'idle');
    if (filter === 'idle')    return workstations.filter(w => !w.stale && w.online && w.current_state === 'idle');
    if (filter === 'offline') return workstations.filter(w => w.stale || !w.online);
    return workstations;
  }, [workstations, filter]);

  const summary = useMemo(() => {
    const online  = workstations.filter(w => !w.stale && w.online).length;
    const idle    = workstations.filter(w => !w.stale && w.online && w.current_state === 'idle').length;
    const offline = workstations.filter(w => w.stale || !w.online).length;
    return { total: workstations.length, online, idle, offline };
  }, [workstations]);

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="workstation-pulse-page">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-zinc-100 flex items-center gap-3">
          <Monitor className="w-6 h-6 text-emerald-400" />
          Workstation Pulse
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Live view of every VOS Studio Agent — what each artist is working on right now.
        </p>
      </header>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
          <p className="text-[11px] text-zinc-500 uppercase tracking-wide">Workstations</p>
          <p className="text-3xl font-semibold text-zinc-100 mt-1">{summary.total}</p>
        </div>
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
          <p className="text-[11px] text-emerald-300 uppercase tracking-wide">Active</p>
          <p className="text-3xl font-semibold text-emerald-400 mt-1">{summary.online - summary.idle}</p>
        </div>
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
          <p className="text-[11px] text-amber-300 uppercase tracking-wide">Idle</p>
          <p className="text-3xl font-semibold text-amber-400 mt-1">{summary.idle}</p>
        </div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
          <p className="text-[11px] text-zinc-500 uppercase tracking-wide">Offline</p>
          <p className="text-3xl font-semibold text-zinc-500 mt-1">{summary.offline}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Workstation grid (2 of 3 cols) */}
        <section className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-3">
            <h2 className="text-sm font-semibold text-zinc-300 flex items-center gap-2">
              <Cpu className="w-4 h-4" /> Workstations
            </h2>
            <div className="flex items-center gap-1.5 ml-auto">
              {[
                { k: 'all',     label: 'All' },
                { k: 'online',  label: 'Active' },
                { k: 'idle',    label: 'Idle' },
                { k: 'offline', label: 'Offline' },
              ].map(b => (
                <button key={b.k}
                  data-testid={`ws-filter-${b.k}`}
                  onClick={() => setFilter(b.k)}
                  className={`px-2.5 py-1 text-[11px] rounded ${filter === b.k ? 'bg-amber-400 text-zinc-900 font-semibold' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}>
                  {b.label}
                </button>
              ))}
              <button onClick={loadWorkstations} title="Refresh"
                data-testid="ws-refresh"
                className="ml-1 p-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white">
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-12 text-center text-sm text-zinc-500">
              Loading workstations…
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950 p-12 text-center" data-testid="ws-empty">
              <Monitor className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-sm text-zinc-300 font-semibold">No workstations registered yet</p>
              <p className="text-xs text-zinc-500 mt-1 max-w-md mx-auto">
                Install VOS Studio Agent on your team's PCs. The MSI installer is on{' '}
                <span className="font-mono text-amber-400">/admin/agent-downloads</span>.
                The moment someone logs in, this dashboard lights up.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filtered.map(ws => <WorkstationCard key={ws.id} ws={ws} />)}
            </div>
          )}

          {lastRefresh && (
            <p className="text-[10px] text-zinc-600 mt-3 flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> Refreshed {relativeTime(lastRefresh.toISOString())}
              · auto-refresh 15 s
            </p>
          )}
        </section>

        {/* DLP feed (1 of 3 cols) */}
        <section className="lg:col-span-1">
          <div className="flex items-center gap-3 mb-3">
            <h2 className="text-sm font-semibold text-zinc-300 flex items-center gap-2">
              <Shield className="w-4 h-4 text-rose-400" /> DLP Event Feed
            </h2>
            <span className="ml-auto text-[10px] text-zinc-500">Last 50</span>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3 max-h-[calc(100vh-300px)] overflow-y-auto space-y-2" data-testid="dlp-feed">
            {dlpEvents.length === 0 ? (
              <div className="text-center py-12 text-xs text-zinc-500">
                <Eye className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                No DLP events yet. Quiet is good. 🟢
              </div>
            ) : (
              dlpEvents.map((e, i) => <DLPRow key={e.id || `${e.ts}-${i}`} evt={e} />)
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
